import type {
	MixedItemDist,
	MixedItemTag,
	PracticeTestExam,
} from "@memorang/types/src/tag";

import { isScreenshotMode, skillIconMap } from "@constants/common";
import { mockedPracticeTestSummary } from "@constants/mocked-data";

/* -----------------Helpers & Hooks--------------- */
import { getNumAttempted, groupTagsByType } from "@helpers/content/tag";
import { useAppStore } from "@hooks/useAppStore";
import type { AppTheme } from "@hooks/useAppTheme";
import {
	formatCount,
	formatNumber,
	smartPluralize,
} from "@memorang/helpers/src/common";
import type { Distribution } from "@memorang/types/src/content";
import type { TFunction } from "i18next";
import pluralize from "pluralize";
import { isI18nKeyExists } from "./common";
import {
	getColorScaleAndDescription,
	getParentMetadata,
} from "./content/metadata";
import type { CurrentView } from "./content/types";
import type { MasteryTagProgressData } from "./content/types";
import { getMappedExams } from "./exams";
import i18n from "./lib/i18n";
import { randomizeDistribution } from "./mocked-helpers";

export type ExamCardData = {
	title: string;
	subtitle: string;
	avgScore: number;
	list: {
		title: string;
		description: string;
		status: string;
		numericId: number;
		cta: string;
		id: string;
		score: number;
	}[];
};

export type SummaryCardData = {
	title: string;
	subtitle: string;
	tooltip: string;
	data: {
		name: string;
		list: {
			icon: string;
			title: string;
			description: string;
			chipLabel: string;
			key?: string;
			tooltip?: string;
		}[];
	}[];
};

type ItemTypeTag = {
	title: string;
	numItems: number;
	distribution: Distribution;
	score: number;
};

type ItemTypeTags = Record<string, ItemTypeTag>;

export const getItemTypeTags = (
	tags: MixedItemTag[],
	t: TFunction,
): ItemTypeTags => {
	const allItems = tags.flatMap((tag) => tag.items);
	const masteryLevels = useAppStore.getState().app.masteryLevels!;
	const groupedItemsByVariant = allItems.reduce<Record<string, MixedItemTag[]>>(
		(acc, item) => {
			if (!item?.variant) {
				return acc;
			}

			acc[item.variant] = acc[item.variant] || [];
			acc[item.variant].push({
				...item,
				title: t(`session.itemTypes.${item.variant}`),
				id: item.id,
				numItems: 0,
				distributionKey: item.distribution,
				score: item.stats?.score,
				distribution: {
					...masteryLevels.reduce((acc, item) => {
						acc[item.id] = 0;
						return acc;
					}, {} as Distribution),
					none: 0,
				},
			});

			return acc;
		},
		{},
	);

	return Object.entries(groupedItemsByVariant).reduce<ItemTypeTags>(
		(acc, [key, items]) => {
			acc[key] = {
				title: key,
				numItems: items.length,
				score: items.reduce((a, c) => a + (c?.score || 0), 0) / items.length,
				distribution: items.reduce<Distribution>(
					(dist, item) => {
						const key = item.distributionKey as keyof Distribution;
						dist[key]++;
						return dist;
					},
					{
						...masteryLevels.reduce((acc, item) => {
							acc[item.id] = 0;
							return acc;
						}, {} as Distribution),
						none: 0,
					},
				),
			};
			return acc;
		},
		{},
	);
};

const getMasteryTagsProgressData = ({
	currentView,
	tags,
	numItems,
	freeTagIds = [],
	hasAccess,
	t,
	theme,
}: {
	currentView: CurrentView;
	tags: MixedItemTag[];
	freeTagIds?: number[];
	hasAccess?: boolean;
	numItems: number;
	t: TFunction;
	theme: AppTheme;
}): MasteryTagProgressData[] => {
	const showPointsBasedProgress =
		useAppStore.getState().reportView?.formative?.showPointsBasedScore;

	const groupedTagsByType = groupTagsByType(
		tags,
		currentView,
		freeTagIds,
		hasAccess,
	);

	const data = Object.keys(groupedTagsByType)
		.filter((item) => {
			return groupedTagsByType[item].length > 0;
		})
		.map((key) => {
			const skillCount = groupedTagsByType[key].length;

			return {
				title: pluralize(key),
				tagType: key.toLowerCase(),
				tooltip: t("progress.tagProgress.tooltip", {
					type: pluralize(key),
					count: skillCount,
					item: smartPluralize("item", numItems),
					itemCount: numItems,
				}),
				cta: `${key ? `View all ${pluralize(key).toLocaleLowerCase()}` : "View all topics"}`,
				subtitle: t("progress.tagProgress.subtitle", {
					numItems,
					type: pluralize(key, skillCount),
					count: skillCount,
					item: smartPluralize("item", numItems),
				}),
				items: groupedTagsByType[key].map((tag) => {
					return {
						title: isI18nKeyExists(`dashboard.skills.${tag.title}`)
							? t(`dashboard.skills.${tag.title}`)
							: tag.title,
						icon: skillIconMap[tag.title],
						iconUrl: tag.iconUrl,
						distribution: isScreenshotMode
							? randomizeDistribution(tag.distribution)
							: tag.distribution,
						numItems: tag.numItems,
						score: showPointsBasedProgress
							? getNumAttempted(tag.distribution)
							: isScreenshotMode
								? Math.floor(Math.random() * (tag.numItems - 8)) + 9
								: tag.numCorrect,
						rightText: t("progress.tagProgress.itemCount", {
							count: tag.numItems || 0,
							item: smartPluralize("item", tag.numItems || 0),
						}),
						...getColorScaleAndDescription({
							theme,
							tag,
							t,
							includeColorScale: true,
						}),
					};
				}),
			};
		});

	return data;
};

const getProgressPageSummaryCardData = (
	response: MixedItemDist,
	numTotalItems: number,
	t: TFunction,
	numItemsAttempted: number,
) => {
	const { distribution } = getParentMetadata(response.contents);
	const numCompletion =
		Object.values(distribution).reduce((a, b) => a + b, 0) - distribution.none;

	const examsWithoutAccess = response.exams?.filter((exam) => !exam.hasAccess);
	const numExamsItemsWithoutAccess =
		examsWithoutAccess?.reduce((acc, exam) => acc + exam.numItems, 0) || 0;

	const accessibleItemsCount = numTotalItems - numExamsItemsWithoutAccess;

	if (!numCompletion) {
		return undefined;
	}

	const masteryLevels = useAppStore
		.getState()
		.app.masteryLevels!.sort((a, b) => a.sortOrder - b.sortOrder);
	const masteryList = masteryLevels.map((item) => {
		return {
			icon: item.icon,
			title: isI18nKeyExists(item.alias.title)
				? t(item.alias.title)
				: item.alias.title,
			iconColor: item.lightColor,
			description: t("progress.mastery.count", {
				count: distribution[item.id],
				item: smartPluralize("item", distribution[item.id]),
			}),
			chipLabel: `${Math.round((distribution[item.id] / numCompletion) * 100)}%`,
			key: item.id,
			// tooltip: t("progress.mastery.highTooltip", {
			// 	count: distribution[item.id],
			// 	item: smartPluralize("item", distribution[item.id]),
			// }),
		};
	});

	const stats = [
		{
			icon: "checkbox-multiple-outline",
			title: t("progress.stats.completion"),
			description: t("progress.stats.completionCount", {
				completed: formatNumber(numCompletion),
				total: formatNumber(accessibleItemsCount),
				item: smartPluralize("item", accessibleItemsCount),
			}),
			chipLabel: `${Math.round((numCompletion / accessibleItemsCount) * 100)}%`,
			tooltip: t("progress.stats.completionTooltip", {
				completed: formatNumber(numCompletion),
				total: formatNumber(accessibleItemsCount),
				item: smartPluralize("item", accessibleItemsCount),
			}),
		},
	];

	const data: SummaryCardData = {
		title: t("progress.summary.title"),
		subtitle: t("progress.summary.subtitle", {
			count: numItemsAttempted,
			item: smartPluralize("item", numItemsAttempted),
		}),
		tooltip: t("progress.summary.tooltip", {
			count: numItemsAttempted,
			item: smartPluralize("item", numItemsAttempted),
		}),
		data: [
			{
				name: t("progress.summary.mastery"),
				list: masteryList,
			},
			{
				name: t("progress.summary.quickStats"),
				list: stats,
			},
		],
	};
	return data;
};

const getNeedsImprovementProgressData = (
	mixedItemDist: MixedItemDist,
	t: TFunction,
	theme: AppTheme,
) => {
	const showPointsBasedProgress =
		useAppStore.getState().reportView?.formative?.showPointsBasedScore;
	const { tags, freeTagIds } = mixedItemDist;
	const groupedTagsByType = groupTagsByType(tags, "Progress", freeTagIds);
	const allTags = Object.keys(groupedTagsByType).flatMap(
		(key) => groupedTagsByType[key],
	);
	const tagType = useAppStore.getState().app.tagType;
	const lowScoredTags = allTags.filter((item) => {
		return Object.entries(item.distribution).some(
			([key, value]) => key !== "none" && value > 0,
		);
	});

	const sortedLowScoredTags = lowScoredTags?.map((tag) => ({
		...tag,
		distribution: tag.distribution,
	}));

	if (sortedLowScoredTags.length) {
		const data = {
			title: t("progress.needsImprovement.title"),
			subtitle: t("progress.needsImprovement.subtitle", {
				type: pluralize(
					tagType
						? pluralize(
								tagType[i18n.language],
								sortedLowScoredTags.length,
							).toLocaleLowerCase()
						: "topic",
				),
			}),
			icon: "chart-gantt",
			items: sortedLowScoredTags.map((tag) => {
				return {
					title: isI18nKeyExists(`dashboard.skills.${tag.title}`)
						? t(`dashboard.skills.${tag.title}`)
						: tag.title,
					distribution: tag.distribution,
					icon: skillIconMap[tag.title],
					numItems: tag.numItems,
					iconUrl: tag.iconUrl,
					score: showPointsBasedProgress
						? getNumAttempted(tag.distribution)
						: isScreenshotMode
							? Math.floor(Math.random() * (tag.numItems - 8)) + 9
							: tag.numCorrect,
					rightText: t("progress.tagProgress.itemCount", {
						count: tag.numItems || 0,
						item: smartPluralize("item", tag.numItems || 0),
					}),
					id: tag.id,
					...getColorScaleAndDescription({
						theme,
						tag,
						t,
						includeColorScale: true,
					}),
				};
			}),
		};
		return data;
	}
	return null;
};

const getPracticeTestsCardsData = (exams?: PracticeTestExam[]) => {
	const filteredExams = exams?.filter(({ status }) => status === "COMPLETE");

	if (!filteredExams?.length) {
		return undefined;
	}
	const avgScore =
		getMappedExams(filteredExams).reduce((a, c) => a + c.score, 0) /
		filteredExams.length;

	const examsCardData: ExamCardData = {
		title: "Practice Tests",
		subtitle: `Based on ${formatCount(
			"completed assessment",
			filteredExams.length,
			true,
		)}`,
		avgScore: Math.round(avgScore),
		list: filteredExams?.length ? getMappedExams(filteredExams) : [],
	};
	return examsCardData;
};

export const hardcodedPracticeTestExams = (
	_examName: string,
	exams: PracticeTestExam[],
) => ({
	title: "Practice Tests",
	subtitle: `Based on ${exams.length} completed practice test`,
	avgScore: 85,
	list: exams.slice(0, 3).map((exam) => ({
		title: exam.title,
		description: `${exam.numItems} items • May 9, 2023 • 50:00`,
		status: "COMPLETE",
		numericId: 15277,
		cta: "View Report",
		id: "B",
		score: Math.floor(Math.random() * 11) + 90,
	})),
});
const getProgressCardsData = (
	data: MixedItemDist,
	currentView: CurrentView,
	hasAccess: boolean,
	t: TFunction,
	theme: AppTheme,
	examName?: string,
) => {
	const numItems = data.contents.reduce((a, c) => a + c.totalCount, 0);

	const numItemsAttempted = data.contents.reduce(
		(a, c) => a + getNumAttempted(c.distribution),
		0,
	);

	const masteryTagsProgressData = getMasteryTagsProgressData({
		currentView,
		tags: data.tags,
		numItems: numItemsAttempted,
		hasAccess,
		freeTagIds: data.freeTagIds,
		t,
		theme,
	});

	const needsImprovementProgressData = getNeedsImprovementProgressData(
		data,
		t,
		theme,
	);

	const showEmptyState = needsImprovementProgressData == null;

	const practiceTestsCardsData = isScreenshotMode
		? hardcodedPracticeTestExams(examName!, data.exams!)
		: getPracticeTestsCardsData(data.exams);

	const progressPageSummaryCardData = isScreenshotMode
		? mockedPracticeTestSummary
		: getProgressPageSummaryCardData(data, numItems, t, numItemsAttempted);
	return {
		masteryTagsProgressData,
		needsImprovementProgressData,
		showEmptyState,
		practiceTestsCardsData,
		progressPageSummaryCardData,
	};
};

export { getProgressCardsData, getMasteryTagsProgressData };
